import { Component, Input } from "@angular/core";
import * as moment from "moment-timezone";

/**
 * Header configuration interface for complex header scenarios
 */
export interface DataTableHeader {
  key: string; // Property key to access in data objects
  label: string; // Display label for the header
  width?: string; // Optional width (e.g., '100px', '20%')
}

@Component({
  selector: "data-table",
  templateUrl: "data-table.component.html",
})
export class DataTableComponent {
  /**
   * Table headers - can be either:
   * - Array of strings (simple column names)
   * - Array of DataTableHeader objects (complex configuration)
   */
  @Input() headers: string[] | DataTableHeader[] = [];

  /**
   * Table data - array of objects where each object represents a row
   */
  @Input() data: any[] = [];

  /**
   * Optional timezone for date formatting (defaults to browser timezone)
   */
  @Input() timezone?: string;

  /**
   * Check if headers are complex objects
   */
  get isComplexHeaders(): boolean {
    return this.headers.length > 0 && typeof this.headers[0] === "object";
  }

  /**
   * Get header label for display
   */
  getHeaderLabel(header: string | DataTableHeader): string {
    if (typeof header === "string") {
      return header;
    }
    return header.label;
  }

  /**
   * Get header key for data access
   */
  getHeaderKey(header: string | DataTableHeader): string {
    if (typeof header === "string") {
      return header;
    }
    return header.key;
  }

  /**
   * Get header width if specified
   */
  getHeaderWidth(header: string | DataTableHeader): string | undefined {
    if (typeof header === "object" && header.width) {
      return header.width;
    }
    return undefined;
  }

  /**
   * Get cell value from row data using header key
   */
  getCellValue(row: any, header: string | DataTableHeader): any {
    const key = this.getHeaderKey(header);
    return row[key] !== undefined ? row[key] : "-";
  }

  /**
   * Check if a value is a date (Date object or valid date string)
   * Detects:
   * - JavaScript Date objects
   * - ISO 8601 date strings (e.g., "2024-01-15T10:30:00Z")
   * - Common date formats that moment can parse
   */
  isDateValue(value: any): boolean {
    if (!value || value === "-") {
      return false;
    }

    // Check if it's a Date object
    if (value instanceof Date) {
      return !isNaN(value.getTime());
    }

    // Check if it's a string that looks like a date
    if (typeof value === "string") {
      // Quick check for common date patterns before using moment
      const datePatterns = [
        /^\d{4}-\d{2}-\d{2}/, // ISO date format (YYYY-MM-DD)
        /^\d{2}\/\d{2}\/\d{4}/, // US date format (MM/DD/YYYY)
        /T\d{2}:\d{2}:\d{2}/, // Time component (ISO format)
      ];

      const looksLikeDate = datePatterns.some((pattern) => pattern.test(value));

      if (looksLikeDate) {
        return moment(value).isValid();
      }
    }

    return false;
  }

  /**
   * Format date for display (date only)
   * Uses MM/DD/YYYY format
   */
  formatDateDisplay(value: any): string {
    if (!value) {
      return "-";
    }

    const tz = this.timezone || moment.tz.guess();
    return moment(value).tz(tz).format("MM/DD/YYYY");
  }

  /**
   * Format date for tooltip (time with timezone)
   * Uses HH:mm z format (e.g., "14:30 CST")
   */
  formatDateTooltip(value: any): string {
    if (!value) {
      return "";
    }

    const tz = this.timezone || moment.tz.guess();
    return moment(value).tz(tz).format("HH:mm z");
  }
}



// WEBPACK FOOTER //
// ./src/components/data-table/data-table.component.ts